/**
 * Filing this device with the server, and forgetting it again (#1642).
 *
 * Platform-agnostic: it asks `pushService` for the OS-specific facts and
 * `api` to carry them. Everything here is about WHEN to register and what to
 * do when it does not work.
 *
 * ⚠️ NOTHING IN THIS FILE THROWS. Registration is a background courtesy, not
 * a step in a flow the person is waiting on: a failure must never break a
 * login, and un-registration must never block a logout. Every path answers a
 * result object, and the caller logs it.
 *
 * ⚠️ UN-REGISTRATION IS A SECURITY STEP, NOT HOUSEKEEPING, which is why
 * `unregisterDevice` is called BEFORE the session is torn down rather than
 * after. A token left filed against a signed-out person is that person's
 * notifications arriving on a handset somebody else is now using — the exact
 * situation `push_devices`' unique index exists to end, and it can only do so
 * if somebody tells the server.
 *
 * ⚠️ THE TOKEN IS CACHED LOCALLY FOR ONE REASON ONLY: logout needs it, and by
 * then the OS may not hand it over again. It is stored with the same
 * AsyncStorage the rest of the app uses and never in encrypted storage,
 * because it is not a credential OF the user — possessing it lets somebody
 * send this handset a notification, not read anything. (`security.md` reserves
 * encrypted storage for tokens that authenticate.)
 *
 * @see src/services/push/pushService.ts
 * @see server/routes/push-devices.route.ts
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import { api } from '../api';
import { pushService } from './pushService';

import type { PushPermissionState } from './pushService';

/** Where the last registered token is remembered, for logout. */
const TOKEN_CACHE_KEY = '@vertiv/push/lastRegisteredToken';

export type RegistrationOutcome =
  | { status: 'registered'; deviceId: number }
  | { status: 'unsupported' }
  | { status: 'permission'; state: PushPermissionState }
  | { status: 'no-token' }
  | { status: 'failed'; reason: string };

/**
 * Why a registration failed, in a form somebody can act on.
 *
 * ⚠️ THE CLASS NAME ALONE IS USELESS, AND THAT COST AN HOUR. This used to
 * answer `error.name`, so every server refusal, every timeout and every
 * unreachable host logged the single word `ApiError` — which says only "the
 * API layer threw", and sends you looking at the server for a request that
 * never left the handset. The status and the message are what separate those
 * three.
 *
 * ⚠️ IT STILL CARRIES NO TOKEN. The message comes from `ApiError`, which is
 * built from the response body; the body of a push-device refusal names the
 * offending FIELD and never echoes the token (`push-devices.route.ts` is
 * explicit about that), so there is nothing here to leak.
 */
const reasonOf = (error: unknown): string => {
  if (!(error instanceof Error)) return 'UnknownError';
  const status = (error as { status?: unknown }).status;
  const prefix =
    typeof status === 'number' ? `${error.name} ${status}` : error.name;
  return error.message ? `${prefix}: ${error.message}` : prefix;
};

const rememberToken = async (token: string): Promise<void> => {
  try {
    await AsyncStorage.setItem(TOKEN_CACHE_KEY, token);
  } catch {
    // A cache miss costs a stale row on the server until the token rotates;
    // failing the registration over it would cost the notifications.
  }
};

const forgetToken = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(TOKEN_CACHE_KEY);
  } catch {
    // Same reasoning as above, in the other direction.
  }
};

export const readCachedToken = async (): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(TOKEN_CACHE_KEY);
  } catch {
    return null;
  }
};

/**
 * Ask for permission if it has not been decided, then file the device.
 *
 * ⚠️ IT ONLY PROMPTS WHEN THE STATE IS UNDECIDED. Re-prompting a person who
 * said no is a prompt the OS will not show anyway on iOS, and on Android it
 * is a dialog they have already dismissed. `checkPermission` first means the
 * person is asked once, at a moment that makes sense.
 */
export const registerDevice = async (): Promise<RegistrationOutcome> => {
  if (!pushService.isSupported) return { status: 'unsupported' };

  try {
    let permission = await pushService.checkPermission();
    if (permission !== 'granted' && permission !== 'blocked') {
      permission = await pushService.requestPermission();
    }
    if (permission !== 'granted') {
      return { status: 'permission', state: permission };
    }

    const token = await pushService.getDeviceToken();
    if (!token) {
      // Normal on a simulator with no APNs registration, and on a build whose
      // Push capability is not provisioned yet. Not an error.
      return { status: 'no-token' };
    }

    const device = await pushService.describeDevice();
    const response = await api.post<{ id: number }>('/push/devices', {
      ...device,
      token,
    });

    await rememberToken(token);
    return { status: 'registered', deviceId: response.id };
  } catch (error) {
    return { status: 'failed', reason: reasonOf(error) };
  }
};

export type UnregisterOutcome =
  | { status: 'removed' }
  | { status: 'nothing-to-remove' }
  | { status: 'failed'; reason: string };

/**
 * Tell the server to forget this handset, and forget the cached token.
 *
 * ⚠️ THE CACHE IS CLEARED EVEN WHEN THE CALL FAILS. If the request did not
 * land, the server row survives — but keeping the token locally would make
 * the NEXT person's login try to unregister a token that is now theirs. The
 * stale row is cleaned up by the dispatcher the first time the provider says
 * the token is gone, or by the next registration moving it.
 */
export const unregisterDevice = async (): Promise<UnregisterOutcome> => {
  if (!pushService.isSupported) return { status: 'nothing-to-remove' };

  const token =
    (await readCachedToken()) ?? (await pushService.getDeviceToken());
  if (!token) return { status: 'nothing-to-remove' };

  try {
    await api.delete('/push/devices', { data: { token } });
    return { status: 'removed' };
  } catch (error) {
    return { status: 'failed', reason: reasonOf(error) };
  } finally {
    await forgetToken();
  }
};

/**
 * Pull the badge number from the server and show it.
 *
 * Called on foreground, because the badge must FALL as the person reads
 * things on another device — a number that only ever grew would be worse
 * than none.
 */
export const syncBadge = async (): Promise<number | null> => {
  if (!pushService.isSupported) return null;
  try {
    const counts = await api.get<{ total: number }>('/push/unread-count');
    await pushService.setBadgeCount(counts.total);
    return counts.total;
  } catch {
    // Leave whatever is on the icon. A failed count must not clear a badge
    // that was right a minute ago — the same rule the server's badge counter
    // follows when it answers `undefined` rather than `0`.
    return null;
  }
};
